import axios from "axios";
import { API_BASEURL } from "../utils/baseURL";
import { authHeader, normalizeAxiosError } from "../utils/headers";

export type VerticalAssignmentOperation = "add" | "remove" | "replace";

//  mirrors the frozen backend wire for the assignment mutation + readback.
export interface VerticalAssignmentResponse {
  videoIndexId: string;
  container: string;
  filename: string;
  folderIds: string[];
  verticalIds: string[];
}

export interface VerticalCurrentAssignment {
  videoIndexId: string;
  verticalIds: string[];
}

//  backend serializes errors as top-level { error, message, details? }
export interface VerticalAssignmentErrorResponse {
  error: string;
  message?: string;
  details?: {
    invalidVerticalIds?: string[];
  };
}

export type VerticalAssignmentServiceResult<T> = {
  status: number;
  data: T | VerticalAssignmentErrorResponse;
  response?: { status: number; data: unknown };
};

const BASE = `${API_BASEURL}v1/videoIndex`;

//  shared boundary in utils/headers already yields a normalized
// `response: { status: 0 }` for response-less network failures; reuse it so
// the dialog can branch on `result.status === 0` without re-throwing.
const normalizeError = (
  error: unknown,
): VerticalAssignmentServiceResult<never> => {
  const normalized = normalizeAxiosError(
    error as Error & { response?: { status: number; data: unknown } },
  );
  const response = (normalized as unknown as {
    response: { status: number; data: unknown };
  }).response;
  return {
    status: response.status,
    data: response.data as VerticalAssignmentErrorResponse,
    response,
  };
};

export const updateVideoVerticalAssignment = async (
  videoIndexId: string,
  verticalIds: string[],
  operation: VerticalAssignmentOperation,
): Promise<VerticalAssignmentServiceResult<VerticalAssignmentResponse>> => {
  const body = {
    videoIndexId,
    verticalIds: Array.from(new Set(verticalIds)),
    operation,
  };
  return await axios
    .put(`${BASE}/${videoIndexId}/verticals`, body, { headers: authHeader() })
    .then((r) => ({ status: r.status, data: r.data as VerticalAssignmentResponse }))
    .catch(normalizeError) as VerticalAssignmentServiceResult<VerticalAssignmentResponse>;
};

export interface BulkVerticalAssignmentResponse {
  videoIndexIds: string[];
  verticalIds: string[];
}

export const updateBulkVideoVerticalAssignments = async (
  videoIndexIds: string[],
  verticalIds: string[],
): Promise<VerticalAssignmentServiceResult<BulkVerticalAssignmentResponse>> => {
  const body = {
    videoIndexIds,
    verticalIds,
  };

  return (await axios
    .put(`${BASE}/verticals`, body, { headers: authHeader() })
    .then((r) => ({ status: r.status, data: r.data as BulkVerticalAssignmentResponse }))
    .catch(normalizeError)) as VerticalAssignmentServiceResult<BulkVerticalAssignmentResponse>;
};

export const getCurrentVideoVerticalAssignment = async (
  videoIndexId: string,
): Promise<VerticalAssignmentServiceResult<VerticalCurrentAssignment>> =>
  await axios
    .get(`${BASE}/${videoIndexId}/verticals`, { headers: authHeader() })
    .then((r) => ({ status: r.status, data: r.data as VerticalCurrentAssignment }))
    .catch(normalizeError) as VerticalAssignmentServiceResult<VerticalCurrentAssignment>;
